import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

const Rule = ({
  id,
  parentId,
  groupParentId,
  field,
  operator,
  operatorValue,
  value,
  translations,
  rules,
  index,
  errors,
  schema: {
    classNames,
    controls,
    createRule,
    fields,
    checkIfFieldInactive,
    isFieldGrouped,
    getInputType,
    getLevel,
    getOperators,
    getValueEditorType,
    getValues,
    onPropChange,
    onRuleRemove,
    onRuleAdd,
    getGroupedFields,
    fetchValues,
    getValueGroupAccessor,
    getCustomGroupValueLabel
  }
}) => {
  const intl = useIntl();
  const onElementChanged = (property, value) => {
    onPropChange(property, value, id);
  };

  const onFieldChanged = (value) => {
    onElementChanged('field', value);
  };

  const onOperatorChanged = (value) => {
    if (value.type === 'placeholder') {
      onElementChanged('operatorValue', value);
    } else {
      onElementChanged('operator', value);
    }
  };

  const onValueChanged = (value) => {
    onElementChanged('value', value);
  };

  const removeRule = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onRuleRemove(id, parentId, groupParentId);
  };

  const addRule = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const newRule = createRule();
    onRuleAdd(newRule, parentId, id);
  };

  let fieldData = fields.find((f) => f.name === field) || null;
  const fieldNotAvailable = !fieldData && !!fields?.length && !!field;
  let deletedField = false;
  const level = getLevel(id);

  let Fields = fields || [];

  const Operators = getOperators(field);

  // Use local copies to avoid reassigning function parameters
  let displayField = field;
  let computedFieldData = fieldData;
  let computedOperators = Operators;
  let computedGetValueEditorType = getValueEditorType;
  let computedGetInputType = getInputType;
  let computedGetValues = getValues;

  if (isFieldGrouped) {
    Fields = getGroupedFields(fields);
  }

  if (fieldNotAvailable && checkIfFieldInactive) {
    const inactiveField = checkIfFieldInactive({ field });
    if (inactiveField) {
      const {
        operators: inactiveFieldOperators,
        fieldData: inactiveFieldData
      } = inactiveField;
      displayField = inactiveFieldData.name;
      computedFieldData = { ...inactiveFieldData, isInactive: true };
      Fields.push(
        isFieldGrouped
          ? {
              label: 'Inactive Fields',
              options: [computedFieldData]
            }
          : computedFieldData
      );
      computedOperators = inactiveFieldOperators;
      computedGetValueEditorType = inactiveField?.getValueEditorType;
      computedGetInputType = inactiveField?.getInputType;
      computedGetValues = inactiveField?.getValues;
    } else {
      deletedField = true;
    }
  }

  return (
    <RuleWrapper
      className={`rule ${classNames.rule}`}
      data-rule-id={id}
      data-level={level}
    >
      {/* <div>{index + 1}</div> */}
      <div id="rule.attribute">
        <controls.fieldSelector
          options={Fields}
          title={translations.fields.title}
          value={displayField}
          operator={operator}
          className={`rule-fields ${classNames.fields}`}
          handleOnChange={onFieldChanged}
          level={level}
          errors={
            errors?.[0]?.errors?.[index]?.error?.name ||
            (deletedField ? [`Field ${displayField} has been deleted`] : null)
          }
          placeholder={<FormattedMessage id="ruleBuilder.fields.placeholder" />}
          isOptionDisabled={(option) => option.isInactive}
        />
      </div>
      <div id="rule.operator">
        <controls.operatorSelector
          field={displayField}
          fieldData={computedFieldData}
          title={translations.operators.title}
          options={computedOperators}
          value={operator || operatorValue}
          className={`rule-operators ${classNames.operators}`}
          handleOnChange={onOperatorChanged}
          level={level}
          errors={errors?.[0]?.errors?.[index]?.error?.operator}
          placeholder={
            <FormattedMessage id="ruleBuilder.operators.placeholder" />
          }
          // disabled={!!fieldNotAvailable}
        />
      </div>
      <div id="rule.value">
        <controls.valueEditor
          field={displayField}
          fieldData={computedFieldData}
          title={translations.value.title}
          operator={operator}
          value={value}
          type={computedGetValueEditorType(displayField, operator)}
          inputType={computedGetInputType(displayField, operator)}
          values={computedGetValues(displayField, operator)}
          className={`rule-value ${classNames.value}`}
          handleOnChange={onValueChanged}
          level={level}
          fetchValues={fetchValues}
          errors={errors?.[0]?.errors?.[index]?.error?.value}
          async
          getValueGroupAccessor={getValueGroupAccessor}
          getCustomGroupValueLabel={getCustomGroupValueLabel}
          // disabled={!!fieldNotAvailable}
        />
      </div>

      <div className="flex center mlauto actions" id="rule-actions">
        <controls.addRuleAction
          label={translations.addRule.label}
          title={translations.addRule.title}
          action={translations.addRule.action}
          className={`ruleGroup-addRule ${classNames.addRule}`}
          handleOnClick={addRule}
          rules={rules}
          level={level}
        />
        <controls.removeRuleAction
          label={translations.removeRule.label}
          title={translations.removeRule.title}
          action={translations.removeRule.action}
          className={`rule-remove ${classNames.removeRule}`}
          handleOnClick={removeRule}
          level={level}
        />
      </div>
    </RuleWrapper>
  );
};

const RuleWrapper = styled.div`
  .actions {
    visibility: hidden;
  }
  &:hover {
    background-color: #f5f5fc !important;
    box-shadow: none !important;
    .actions {
      visibility: visible;
    }
  }
`;

Rule.defaultProps = {
  id: null,
  parentId: null,
  field: null,
  operator: null,
  value: null,
  schema: null
};

Rule.displayName = 'Rule';

export default Rule;
